import { useEffect, useRef, useState } from "react";
import Icon from "../Icon";
import { BtnGhost } from "../Atoms";
import SelectorTarea from "./SelectorTarea";
import { AG_ORIGENES } from "../../constants";
import { getTipoColor, getTipoIcon, fmtHours } from "../../utils";
import * as api from "../../api";
import { aplanarMisTareas } from "../misTareas/pool";

// «¿Qué estoy haciendo ahora?» — el cronómetro de tarea del panel de inicio.
//
// El tiempo NO se guarda en el navegador: el cronómetro en curso y los que
// quedan en pausa viven en el servidor. Cerrar la pestaña, recargar o entrar
// desde otro equipo da el mismo número, y al final del día ese tiempo aterriza
// en el Cuaderno con una entrada por tarea.
//
// Solo corre UNO a la vez, a propósito: cambiar de tarea pausa la anterior. El
// caso que motiva todo esto es la interrupción — entra una incidencia, se para
// lo que había y se atiende.

// Mismo formato que el widget de fichaje, que está justo encima: dos contadores
// contiguos con formatos distintos se leen mal.
const duracion = ms => {
  const t = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(t / 3600)).padStart(2, "0")}:`
       + `${String(Math.floor(t % 3600 / 60)).padStart(2, "0")}:`
       + `${String(t % 60).padStart(2, "0")}`;
};

export default function CronometroWidget({
  crono, fichaje, agendaTiposData = [], misPasos = [], notionItems = [],
  verIncidencias = false, onAbrirPanel, onCerrarJornada, senalElegir = 0,
}) {
  const [eligiendo, setEligiendo] = useState(false);
  const [ahora, setAhora] = useState(Date.now());
  // Lo que hay en Mis tareas, para el selector unico. Null = no se ve (403/404, migracion o
  // fallo): el selector se queda como estaba, con los dos buscadores de siempre.
  const [pool, setPool] = useState(null);
  // Tras empezar una tarea de fuera: { tipo: "ok", id, titulo } (alta hecha, con Deshacer) o
  // { tipo: "fallo", traer } (el alta fallo; el cronometro sigue y se puede reintentar).
  const [aviso, setAviso] = useState(null);
  const temporizador = useRef(null);
  useEffect(() => () => clearTimeout(temporizador.current), []);

  useEffect(() => {
    if (!eligiendo) return undefined;
    let vivo = true;
    api.getMisTareas()
      .then(d => { if (vivo) setPool(d?.migracion_pendiente ? null : aplanarMisTareas(d)); })
      .catch(() => { if (vivo) setPool(null); });
    return () => { vivo = false; };
  }, [eligiendo]);

  // El cronometro arranca PRIMERO; esto va despues y un fallo no lo detiene.
  const traer = async t => {
    clearTimeout(temporizador.current);
    try {
      const r = await api.traerAMisTareas({ origen: t.origen, ...(t.origen === "paso" ? { task_id: t.id } : { external_id: t.id }) });
      if (r?.creada && r.id != null) {
        setAviso({ tipo: "ok", id: r.id, titulo: t.titulo });
        temporizador.current = setTimeout(() => setAviso(null), 8000);
      } else setAviso(null);
    } catch {
      setAviso({ tipo: "fallo", traer: t });
    }
  };
  const deshacerTraida = async () => {
    const a = aviso;
    clearTimeout(temporizador.current);
    setAviso(null);
    if (a?.tipo === "ok") { try { await api.deshacerTraidaMisTarea(a.id); } catch { /* ya no estaba */ } }
  };

  const dentro = fichaje.estado === "dentro";
  const c = crono.activo;

  // El tick vive aquí y no en el hook para que solo re-renderice esta tarjeta.
  useEffect(() => {
    if (!c) return;
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [c]);

  // «Cambiar de tarea» desde la cabecera o desde una ficha de incidencia pausa
  // allí y manda aquí: sin abrir el buscador, el gesto se quedaba a medias y
  // había que volver a pulsar «Empezar una tarea» nada más aterrizar.
  useEffect(() => { if (senalElegir) setEligiendo(true); }, [senalElegir]);

  // Al fichar salida se cierra el panel de elección: dejar abierto un formulario
  // que el servidor va a rechazar es peor que no ofrecerlo.
  useEffect(() => { if (!dentro) setEligiendo(false); }, [dentro]);

  // Al servidor le falta la migración: el widget desaparece y el resto del panel
  // sigue funcionando igual.
  if (crono.disponible === false) return null;

  const card = {
    background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 13,
    boxShadow: "var(--shadow-sm)", padding: 16,
    display: "flex", flexDirection: "column", gap: 12,
  };
  const titulo = {
    fontWeight: 700, fontSize: 11, color: "var(--text2)", textTransform: "uppercase",
    letterSpacing: ".07em", display: "flex", alignItems: "center", gap: 6,
  };

  const cabecera = (
    <div style={titulo}>
      <Icon name="hourglass-split" size={13} color="var(--accent)" />
      ¿Qué estoy haciendo ahora?
      {/* Si se descartó el cierre de jornada, el tiempo se queda en pausa para
          siempre sin llegar al cuaderno. Este aviso es la vuelta a ese camino. */}
      {crono.pendienteCierre && crono.hayAbiertos && (
        <button className="btn" onClick={onCerrarJornada}
          style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5,
            padding: "3px 11px", borderRadius: 20, border: "1px solid var(--warn-line)",
            background: "var(--warn-bg)", color: "var(--warn-text)", cursor: "pointer",
            fontSize: 12, fontWeight: 700, fontFamily: "var(--font)" }}>
          <Icon name="journal-text" size={12} color="#a16207" />Pendiente de anotar
        </button>
      )}
      {!crono.pendienteCierre && crono.minutosHoy > 0 && (
        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "var(--accent)" }}>
          {fmtHours(crono.minutosHoy / 60)} hoy
        </span>
      )}
    </div>
  );

  // ── Cargando ───────────────────────────────────────────────────────────────
  if (crono.disponible === null && crono.cargando) {
    return (
      <div style={card}>
        {cabecera}
        <div style={{ fontSize: 12, color: "var(--text3)" }}>Consultando tus tareas…</div>
      </div>
    );
  }

  // ── Sin Odoo conectado ─────────────────────────────────────────────────────
  // El formulario de conexión es del widget de fichaje, que está justo al lado:
  // repetirlo aquí serían dos sitios donde escribir la misma contraseña.
  if (fichaje.conectado !== true) {
    return (
      <div style={card}>
        {cabecera}
        <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.5 }}>
          Conecta tu cuenta de Odoo y ficha entrada para poder cronometrar tus tareas.
          El tiempo tiene que caer dentro de una jornada.
        </div>
      </div>
    );
  }

  const botonAccion = {
    padding: "9px 14px", borderRadius: 9, border: "none", cursor: "pointer",
    fontSize: 12.5, fontWeight: 700, color: "#fff", fontFamily: "var(--font)",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    opacity: crono.guardando ? .6 : 1,
  };

  // ── Los borradores en pausa ────────────────────────────────────────────────
  // Se listan también cuando no estás dentro (en solo lectura): es exactamente
  // el estado de quien fichó salida y descartó el cierre de jornada.
  const listaPausados = !crono.pausados.length ? null : (
    <div style={{ borderLeft: "1px solid var(--border2)", paddingLeft: 14, minWidth: 0 }}>
      <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 7,
        textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 700 }}>
        En pausa · <span style={{ color: "var(--accent)", fontWeight: 800 }}>
          {fmtHours(crono.pausados.reduce((s, p) => s + crono.msDe(p, ahora), 0) / 3600000)}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {crono.pausados.map(p => {
          const cl = getTipoColor(p.type, [], agendaTiposData);
          return (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8,
              padding: "6px 8px", borderRadius: 8, background: "var(--bg)" }}>
              <span style={{ width: 7, height: 7, borderRadius: 2, background: cl, flexShrink: 0 }} />
              <span title={p.description} style={{ flex: 1, minWidth: 0, fontSize: 12,
                fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis",
                whiteSpace: "nowrap" }}>{p.description}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: cl, whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums" }}>{duracion(crono.msDe(p, ahora))}</span>
              {dentro && (
                <button aria-label="Reanudar esta tarea" className="btn" onClick={() => crono.reanudar(p.id)}
                  disabled={crono.guardando} title="Reanudar esta tarea"
                  style={iconBtn}>
                  <Icon name="play-fill" size={14} color="var(--ok-text)" />
                </button>
              )}
              <button aria-label="Anotar en el cuaderno y cerrar" className="btn" onClick={() => crono.parar(p.id)}
                disabled={crono.guardando} title="Anotar en el cuaderno y cerrar"
                style={iconBtn}>
                <Icon name="journal-text" size={12} color="var(--text3)" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Zona principal ─────────────────────────────────────────────────────────
  let principal;

  if (eligiendo) {
    principal = (
      <SelectorTarea
        agendaTiposData={agendaTiposData} misPasos={misPasos} notionItems={notionItems}
        poolMisTareas={pool}
        verIncidencias={verIncidencias} guardando={crono.guardando}
        onAbrirPanel={onAbrirPanel}
        onCancelar={() => setEligiendo(false)}
        onEmpezar={async (d, extra) => {
          if (await crono.iniciar(d)) { setEligiendo(false); if (extra?.traer) traer(extra.traer); }
        }} />
    );
  } else if (c) {
    const cl = getTipoColor(c.type, [], agendaTiposData);
    const ic = getTipoIcon(c.type, agendaTiposData);
    const org = AG_ORIGENES[c.origen];
    principal = (
      <div>
        {/* Primero QUÉ, luego cuánto: el título es lo que distingue dos
            cronómetros del mismo tipo. */}
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6, minWidth: 0 }}>
          {ic ? <Icon name={ic} size={12} color={cl} />
              : <span style={{ width: 7, height: 7, borderRadius: "50%", background: cl, flexShrink: 0 }} />}
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {c.description}
          </span>
          {c.origen !== "ninguno" && org && (
            <span title={c.project_name || org.hint}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0,
                padding: "1px 7px", borderRadius: 20, background: cl + "18",
                border: `1px solid ${cl}44`, fontSize: 9.5, fontWeight: 700, color: cl,
                maxWidth: 200, overflow: "hidden" }}>
              <Icon name={org.icon} size={9} color={cl} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.incidencia_ref ? `#${c.incidencia_ref}` : (c.etiqueta || org.label)}
              </span>
            </span>
          )}
        </div>

        <div style={{ fontSize: 30, fontWeight: 800, color: cl, letterSpacing: "-.02em",
          fontVariantNumeric: "tabular-nums" }}>
          {duracion(crono.msDe(c, ahora))}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
          {c.type}{c.tramos > 1 && ` · ${c.tramos} tramos hoy`}
        </div>

        <div style={{ display: "flex", gap: 7, marginTop: 11, flexWrap: "wrap" }}>
          <button onClick={() => crono.pausar(c.id)} disabled={crono.guardando}
            style={{ ...botonAccion, background: "#f59e0b" }}>
            <Icon name="pause-circle-fill" size={14} color="#fff" />Pausar
          </button>
          {/* Interrupción en un solo gesto: pausa y abre el buscador. En dos
              pasos cuesta segundos justo cuando hay prisa. */}
          <button disabled={crono.guardando}
            onClick={async () => { await crono.pausar(c.id); setEligiendo(true); }}
            style={{ ...botonAccion, background: "var(--accent)" }}>
            <Icon name="arrow-repeat" size={14} color="#fff" />Cambiar de tarea
          </button>
          <BtnGhost onClick={() => crono.parar(c.id)}>Terminar y anotar</BtnGhost>
        </div>
      </div>
    );
  } else {
    principal = (
      <div>
        <div style={{ fontSize: 30, fontWeight: 800, color: "var(--text3)",
          letterSpacing: "-.02em", fontVariantNumeric: "tabular-nums" }}>00:00:00</div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
          Sin tarea en marcha
        </div>

        {dentro ? (
          <button onClick={() => setEligiendo(true)}
            style={{ ...botonAccion, background: "var(--green)", width: "100%",
              padding: "12px 0", fontSize: 13.5, marginTop: 11 }}>
            <Icon name="play-fill" size={16} color="#fff" />Empezar una tarea
          </button>
        ) : (
          // El candado real está en el servidor; esto solo evita ofrecer un
          // botón que iba a devolver un 409.
          <div style={{ marginTop: 11, padding: "8px 11px", borderRadius: 9,
            border: "1px solid var(--warn-line)", background: "var(--warn-bg)", fontSize: 11.5,
            color: "var(--warn-text)", display: "flex", alignItems: "flex-start", gap: 6,
            lineHeight: 1.45 }}>
            <Icon name="exclamation-triangle-fill" size={12} color="#a16207"
              style={{ marginTop: 1, flexShrink: 0 }} />
            <span>Ficha entrada para empezar a cronometrar. El tiempo tiene que
              caer dentro de tu jornada.</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={card}>
      {cabecera}
      {/* .grid-1col-tab ya existe y apila en ≤1024 y ≤768: sin CSS nuevo. */}
      <div className={listaPausados ? "grid-1col-tab" : undefined}
        style={listaPausados
          ? { display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 14 }
          : undefined}>
        {principal}
        {listaPausados}
      </div>
      {aviso?.tipo === "ok" && (
        <div className="mt-sel-aviso ok" role="status">
          <span>Añadida a Mis tareas como pendiente.</span>
          <button type="button" className="btn" onClick={deshacerTraida}>Deshacer</button>
        </div>
      )}
      {aviso?.tipo === "fallo" && (
        <div className="mt-sel-aviso fallo" role="alert">
          <span><b>Empezada, pero no se pudo añadir a Mis tareas.</b> El cronómetro sigue.</span>
          <button type="button" className="btn" onClick={() => traer(aviso.traer)}>Reintentar</button>
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "transparent", border: "none", cursor: "pointer", padding: 0, display: "flex",
};
